import { Button, Col, Layout, Row } from "antd";
import moment from "moment";
import momentTz from "moment-timezone";
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styled from "styled-components";
import { useQuery } from "urql";
import {
  GET_BOOKED_DATES,
  GET_BOOKED_TIMES,
} from "src/graphql/queries/schedule";
import { Timer, disabledTime } from "src/components/utils/Timer";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";
import { logEvent } from "src/analytics/amplitude";
import { getNextAvailableDate } from "src/weirdStuff/date";
import { useTasks } from "src/context/tasks";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import MobileNavbar from "src/components/utils/MobileNavbar";

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const ScheduleNote = styled.p`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin: 16px 0;
`;

const SchedulingGrid = styled(Row)`
  @media screen and (min-width: 992px) {
    display: flex;
    align-items: stretch;

    .ant-col {
      display: flex;
      flex-direction: column;
    }

    .calendar-wrapper,
    .timer-wrapper {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .calendar {
      flex: 1;
      height: 100%;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  margin-bottom: 24px;

  @media screen and (max-width: 500px) {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  min-height: 44px;
  padding: 0 24px;
  border-radius: 6px;
  font-weight: 500;
`;

const PreviousButton = styled(StyledButton)`
  border: 1px solid #e2e2e2;
  color: #666;

  &:hover {
    border-color: #d3d3d3;
    color: #373744;
  }
`;

const NextButton = styled(StyledButton)`
  background-color: var(--blue-300);
  color: white;
  border: none;

  &:hover {
    background-color: var(--blue-400);
  }

  &:disabled {
    background-color: #e2e2e2;
    color: #999;
  }
`;

interface TaskSchedulingProps {
  changeTaskPage: (page: number) => void;
}

export default function TaskScheduling({
  changeTaskPage,
}: TaskSchedulingProps) {
  const { selectedTasks, scheduleInfo, setScheduleInfo } = useTasks();
  const width = useResizeObserver();
  const isMobile = width <= 500;

  const [taskDate, setTaskDate] = useState<string>(scheduleInfo.date);
  const [taskTime, setTaskTime] = useState<string>(scheduleInfo.time);
  const [instructions] = useState<string>(scheduleInfo.instructions);

  // Redirect if no tasks selected
  useEffect(() => {
    if (selectedTasks.length === 0) {
      changeTaskPage(1);
    }
  }, [selectedTasks, changeTaskPage]);

  const [{ data: bookedDatesData, fetching: loadingBookedDates }] = useQuery<{
    bookedDates: string[];
  }>({
    query: GET_BOOKED_DATES,
  });

  useEffect(() => {
    if (!taskDate && bookedDatesData?.bookedDates?.length) {
      const date = moment(
        getNextAvailableDate(taskDate, bookedDatesData?.bookedDates)
      ).format("YYYY-MM-DD");
      setTaskDate(date);
    }
  }, [bookedDatesData?.bookedDates, taskDate]);

  const [{ data: bookedTimesData, fetching: loadingBookedTimes }] = useQuery<{
    bookedTimes: string[];
  }>({
    query: GET_BOOKED_TIMES,
    variables: {
      date: taskDate,
    },
    pause: !taskDate,
  });

  const handleDateChange = (date: string) => {
    setTaskDate(date);
    setTaskTime("");
  };

  const handleTimeChange = (time: string) => {
    setTaskTime(time);
  };

  const handlePrevious = () => {
    // Save current state before navigating back
    setScheduleInfo({
      date: taskDate,
      time: taskTime,
      instructions: instructions.trim(),
    });
    changeTaskPage(1);
  };

  const handleNext = () => {
    if (taskDate && taskTime) {
      // Save schedule info to context
      setScheduleInfo({
        date: taskDate,
        time: taskTime,
        instructions: instructions.trim(),
      });

      logEvent("User scheduled tasks", {
        taskDate,
        taskTime: taskTime,
        tasksCount: selectedTasks.length,
        hasInstructions: !!instructions.trim(),
      });

      // Navigate to contact info page
      changeTaskPage(3);
    } else if (bookedDatesData?.bookedDates?.includes(taskDate)) {
      openNotificationWithIcon(
        "error",
        "Selected date is not available",
        "Please select another date"
      );
    } else if (
      bookedTimesData?.bookedTimes?.length &&
      disabledTime(taskTime, bookedTimesData?.bookedTimes)
    ) {
      openNotificationWithIcon(
        "error",
        "Selected time is not available",
        "Please select another time"
      );
    } else {
      let notification = { description: "", title: "" };
      if (!taskDate && !taskTime) {
        notification.description = "Please select the date and time";
        notification.title = "Error";
      } else if (!taskDate) {
        notification.title = "Please select a date";
        notification.description = "Please select a date for your tasks";
      } else if (!taskTime) {
        notification.title = "Please select a time";
        notification.description = "Please select a time for your tasks";
      }
      openNotificationWithIcon(
        "error",
        notification.title,
        notification.description
      );
    }
  };

  if (loadingBookedDates) {
    return (
      <Layout className="step-layout mobile-step-layout">
        <LoadingContainer>
          <p>Loading available schedules...</p>
        </LoadingContainer>
      </Layout>
    );
  }

  return (
    <Layout className="step-layout mobile-step-layout">
      <h1 className="semi-bold">Schedule Your Tasks</h1>
      <br />

      <SchedulingGrid gutter={16}>
        <Col lg={12} xs={24}>
          <div className="calendar-wrapper">
            <p>
              <strong>Confirm Date</strong>
            </p>
            <Calendar
              calendarType="US"
              value={
                taskDate
                  ? momentTz.tz(taskDate, "America/New_York").toDate()
                  : undefined
              }
              tileDisabled={({ date }) =>
                bookedDatesData?.bookedDates?.includes(
                  moment(date).format("YYYY-MM-DD")
                ) || false
              }
              onChange={(v) => handleDateChange(moment(v).format("YYYY-MM-DD"))}
              minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 2))}
              className="calendar"
              next2Label={isMobile ? null : undefined}
              prev2Label={isMobile ? null : undefined}
            />
          </div>
        </Col>
        <Col lg={12} xs={24}>
          <div className="timer-wrapper">
            <p>
              <strong>Confirm Time</strong>
            </p>
            <Timer
              disabledTimes={bookedTimesData?.bookedTimes}
              isLoading={loadingBookedTimes}
              isDisabled={!taskDate}
              isFlexible={false}
              hideFlexibleTime={true}
              cleanTime={taskTime}
              onTimeChange={handleTimeChange}
            />
          </div>
        </Col>
      </SchedulingGrid>

      {taskTime ? (
        <ScheduleNote>
          Our team will arrive on selected date at {taskTime}
        </ScheduleNote>
      ) : null}

      {isMobile ? (
        <MobileNavbar
          prevAction={handlePrevious}
          nextAction={handleNext}
          nextText="Continue"
          type="task_booking"
          prevText="Back"
        />
      ) : (
        <ButtonGroup>
          <PreviousButton onClick={handlePrevious}>Previous</PreviousButton>
          <NextButton
            type="primary"
            onClick={handleNext}
            disabled={!taskDate || !taskTime}
          >
            Continue
          </NextButton>
        </ButtonGroup>
      )}
    </Layout>
  );
}
